import SlIconButton from '@shoelace-style/shoelace/components/icon-button/icon-button.component';
import {notNull} from '../../../../../library/clxProductNet/assets/js/modules/common/assert';
import ShoelaceFormValidator from '../../../../../library/clxProductNet/assets/js/modules/common/shoelaceFormValidator';
import AnalyticsTracker from '../../../../../library/clxProductNet/assets/js/modules/common/tracking/analyticsTracker';
import BrowserTabUidManager from '../../../../../library/clxProductNet/assets/js/modules/common/tracking/browserTabUidManager';
import MicroConversionTracker from '../../../../../library/clxProductNet/assets/js/modules/common/tracking/microConversionTracker';
import type {SlAlert, SlButton, SlCheckbox, SlRadioGroup} from '../../../../../vendor/plugins/clxFrontendCms/public/js/shoelace/shoelace';
import {RequestFailedError} from '../common/errors';
import {getLocale} from '../common/locale';
import {EventCategoryEnum} from '../common/trackingFrontend/Model/EventCategoryEnum';
import {EventProductCustomizationEnum} from '../common/trackingFrontend/Model/EventProductCustomizationEnum';
import {MicroConversionTrackingEventsEnum} from '../common/trackingMicroconversion/Model/EventProductCustomizationEnum';
import CustomizationOrderFormMapper from './Integration/customizationOrderFormMapper';
import CustomizationOrderSubmitMapper from './Integration/customizationOrderSubmitMapper';
import ProductCustomizationConfigModel from './Model/productCustomizationConfigModel';
import ProductCustomizationTrackingModel from './Model/productCustomizationTrackingModel';

export default class ProductCustomizationOrder {
    private readonly microConversionTracker: MicroConversionTracker;

    private readonly browserTabUidManager: BrowserTabUidManager;

    html: HTMLElement;

    closeCutomizationOrderFormButton: HTMLDivElement | undefined;

    backToCustomizationButton: SlIconButton | undefined;

    customizationOrderOverlay: HTMLDivElement | undefined;

    urlEmailValidation: string;
    private customizerContainer: HTMLDivElement;

    private submitButton: SlButton | undefined;

    private readonly config: ProductCustomizationConfigModel;

    private readonly _tracking: AnalyticsTracker;
    private customizationEmailCheckMapper: CustomizationOrderSubmitMapper;
    private readonly customizationOrderFormMapper: CustomizationOrderFormMapper;

    constructor(config: ProductCustomizationConfigModel) {
        this.browserTabUidManager = new BrowserTabUidManager();
        this.microConversionTracker = new MicroConversionTracker(`/${getLocale()}/product-customization/apiTrack/`);

        this.config = config;
        this.html = notNull(document.querySelector('html'));
        this.customizerContainer = notNull(document.querySelector<HTMLDivElement>('#customization-order-container'));

        this._tracking = new AnalyticsTracker();
        this.customizationEmailCheckMapper = new CustomizationOrderSubmitMapper();

        // All URL's for ajax actions
        this.urlEmailValidation = `/${getLocale()}/product-customization-order/apiEmailCheck/email/`;

        this.customizationOrderFormMapper = new CustomizationOrderFormMapper();
    }

    async _openCustomizationOrderForm() {
        const response = await this.customizationOrderFormMapper.fetchCustomOrderForm(this.config, this.browserTabUidManager.createBrowsertabUid());

        if (response instanceof RequestFailedError) {
            return;
        }

        this.customizerContainer.innerHTML = response;
        this.customizationOrderOverlay = notNull(this.customizerContainer.querySelector<HTMLDivElement>('.product-customization-order-overlay'));
        this._attachOverlayEventListeners();

        this.customizationOrderOverlay.classList.add('open');

        this._tracking.fireMatomoTrackingEvent(EventCategoryEnum.productCustomization, EventProductCustomizationEnum.openCustomizationOrderForm);
        let trackingModel = new ProductCustomizationTrackingModel(this.config);
        this.microConversionTracker.trackWithBeacon(MicroConversionTrackingEventsEnum.customization_go_order, trackingModel.getAsJsonStringForTracking());
    }

    private _attachOverlayEventListeners() {
        this.submitButton = notNull(this.customizerContainer.querySelector<SlButton>('sl-button#submitOrder'));

        this._attachDeliveryAddressToggleEvent();
        this._attachShippingOrDeliveryAddressToggleEvent();
        this._attachAgbCheckboxEvent();

        this._submitOrderEvent();

        this.closeCutomizationOrderFormButton = notNull(
            this.customizerContainer.querySelector<HTMLDivElement>('.product-customization-order-overlay .close-overlay'),
        );
        this.closeCutomizationOrderFormButton!.addEventListener('click', async (e) => {
            await this._closeCustomizerOrderform();
        });

        this.backToCustomizationButton = notNull(document.querySelector<SlIconButton>('sl-icon-button.back-to-customization'));
        this.backToCustomizationButton!.addEventListener('click', async (e) => {
            await this._closeCustomizerOrderform();
        });
    }

    private _attachAgbCheckboxEvent(): void {
        const agbCheckBox = document.querySelector<SlCheckbox>('sl-checkbox[name="agb"]');
        const agbAlert = document.getElementById('agb_alert') as SlAlert;
        if (agbCheckBox) {
            agbCheckBox.addEventListener('sl-change', () => {
                if (agbCheckBox.checked) {
                    agbAlert.hide();
                }
            });
        }
    }

    private _attachDeliveryAddressToggleEvent() {
        const separateAdlToggle = this.customizerContainer.querySelector<SlRadioGroup>('.product-customization-order-overlay .separate-adl');
        if (!separateAdlToggle) {
            return;
        }

        const separateAdlForm = this.customizerContainer.querySelector<HTMLFormElement>('#delivery-edit');

        separateAdlToggle.addEventListener('sl-change', async (event) => {
            if (separateAdlToggle.value === '1') {
                separateAdlForm!.style.display = 'grid';
            } else {
                separateAdlForm!.style.display = 'none';
            }
        });
    }

    private _attachShippingOrDeliveryAddressToggleEvent() {
        const shippingOrDeliveryToggle = this.customizerContainer.querySelector<SlRadioGroup>('.product-customization-order-overlay .shipping_address');

        if (!shippingOrDeliveryToggle) {
            return;
        }

        const deliveryAddressContainer = this.customizerContainer.querySelector<HTMLFormElement>('#lieferadresse');
        const shippingAddressContainer = this.customizerContainer.querySelector<HTMLFormElement>('#versandadresse');
        const shippingFormContainer = this.customizerContainer.querySelector<HTMLFormElement>('#versandadresse #delivery-edit');

        shippingOrDeliveryToggle.addEventListener('sl-change', async (event) => {
            if (shippingOrDeliveryToggle.value === 'lager') {
                shippingAddressContainer!.style.display = 'none';
                deliveryAddressContainer!.style.display = 'block';
            } else {
                deliveryAddressContainer!.style.display = 'none';
                shippingFormContainer!.style.display = 'grid';
                shippingAddressContainer!.style.display = 'unset';
            }
        });
    }

    private _submitOrderEvent() {
        const orderForm = this.customizerContainer.querySelector<HTMLFormElement>('#customization-order-form');
        if (orderForm) {
            const orderFormValidator = new ShoelaceFormValidator(orderForm, `${getLocale()}`, this.urlEmailValidation);
            orderFormValidator.attachListener();

            this.submitButton!.addEventListener('click', async (event) => {
                event.preventDefault();
                const agbCheckBox = document.querySelector<SlCheckbox>('sl-checkbox[name="agb"]');
                const agbAlert = document.getElementById('agb_alert') as SlAlert;
                if (agbCheckBox && !agbCheckBox.checked) {
                    agbAlert.show();
                    return;
                }

                const response = await this.customizationEmailCheckMapper.submitCustomizationOrderForm(orderForm.action, this.createMergedFormData(orderForm));

                if (response instanceof RequestFailedError) {
                    return;
                }

                if (!orderFormValidator.checkServerValidation(response)) {
                    orderFormValidator.processServerValidationResponse(response);
                    return;
                }

                let trackingModel = new ProductCustomizationTrackingModel(this.config);
                this.microConversionTracker.trackWithBeacon(
                    MicroConversionTrackingEventsEnum.customization_send_order,
                    trackingModel.getAsJsonStringForTracking(),
                );
                this._tracking.fireMatomoTrackingEvent(EventCategoryEnum.productCustomization, EventProductCustomizationEnum.sendCustomizationOrderForm);

                if (response.redirect) {
                    location.href = response.redirect;
                    return;
                }
            });
        }
    }

    private createMergedFormData(orderForm: HTMLFormElement): FormData {
        const configFormData = notNull(this.config).getAsFormdata();
        const formData = new FormData(orderForm);
        configFormData.append('browserTabUid', this.browserTabUidManager.createBrowsertabUid());
        for (const [key, value] of formData.entries()) {
            configFormData.append(key, value);
        }

        return configFormData;
    }

    _closeCustomizerOrderform() {
        this.customizationOrderOverlay!.classList.remove('open');
        this._tracking.fireMatomoTrackingEvent(EventCategoryEnum.productCustomization, EventProductCustomizationEnum.closeCustomizationOrderForm);

        let trackingModel = new ProductCustomizationTrackingModel(this.config);
        this.microConversionTracker.trackWithBeacon(MicroConversionTrackingEventsEnum.customization_close_order, trackingModel.getAsJsonStringForTracking());
    }
}
